import React from 'react';
import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import { useVendorPartnerCandidates } from '~/hooks/vendors/useVendorPartnerCandidates';
import { SearchInput } from '~/components/common/parts/search/SearchInput';
import type { VendorCompany } from '~/models/VendorCompany';
import { VendorPartnerCandidateRow } from '~/components/specifics/vendors/settings/vendor_partners/VendorPartnerCandidateRow';
import { create } from '~/utils/api/vendors/VendorPartner';
import { isSuccess } from '~/utils/api/api_handler';

type Props = DeepReadonly<{
  isOpen: boolean;
  handleClose: () => void;
  handleMutate: () => Promise<void>;
}>;

export const VendorPartnerAddModal: React.FC<Props> = ({
  isOpen,
  handleClose,
  handleMutate
}: Props) => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [searchWord, setSearchWord] = React.useState<string>('');
  const { data: vendorCompanyCandidates, mutate: mutateCandidates } =
    useVendorPartnerCandidates().index(searchWord);
  const handleChangeSelectedIds = React.useCallback(
    (newSelectedIds: number[]) => {
      setSelectedIds(newSelectedIds);
    },
    []
  );
  const handleChangeSearchWord = React.useCallback((newSearchWord: string) => {
    setSearchWord(newSearchWord);
  }, []);
  const renderRow = React.useCallback(
    (vendorCompany: VendorCompany) => {
      if (!vendorCompany.name.includes(searchWord)) return null;
      return (
        <VendorPartnerCandidateRow
          key={vendorCompany.id}
          handleChangeSelectedIds={handleChangeSelectedIds}
          selectedIds={selectedIds}
          vendorCompany={vendorCompany}
        />
      );
    },
    [handleChangeSelectedIds, searchWord, selectedIds]
  );
  const handleSubmit = React.useCallback(async () => {
    const response = await create(selectedIds);
    if (isSuccess(response)) {
      await mutateCandidates?.().then();
      await handleMutate();
      toast.success('協力会社を追加しました。');
      handleClose();
      return;
    }
    toast.error('エラーが発生しました');
  }, [selectedIds, mutateCandidates, handleMutate, handleClose]);
  return (
    <Modalv2 isOpen={isOpen} onClose={handleClose}>
      <ModalHeader title="協力会社の追加" />
      <ModalBody>
        <SearchInput handleChange={handleChangeSearchWord} />
        {vendorCompanyCandidates?.map(renderRow)}
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button onClick={handleClose} variant="secondary">
            保存せず閉じる
          </Button>
        }
        rightArea={
          <Button onClick={handleSubmit} variant="primary">
            保存
          </Button>
        }
      />
    </Modalv2>
  );
};
